/**
 * Route Sharing Service
 * 
 * Enables managers to share optimized routes with team members.
 * Routes are stored in Firestore and can be loaded by other users.
 * 
 * Features:
 * - Create shareable route links
 * - Send routes to specific users
 * - Push notifications for shared routes
 * - Route expiration (24 hours default)
 */

import { db } from '../firebase/client';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy, 
  Timestamp,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { getCompanyId } from '../tenantContext';
import { toast } from 'sonner';

// ============================================================================
// TYPES
// ============================================================================

export interface SharedRoute {
  id: string;
  name: string;
  description?: string;
  
  // Route data
  wells: SharedRouteWell[];
  totalDistance: number; // miles
  estimatedTime: number; // hours
  optimizationMetrics?: {
    algorithm: string;
    improvementPercent: number;
    validationIssues: number;
  };
  
  // Metadata
  createdBy: {
    uid: string;
    name: string;
    role?: string;
  };
  createdAt: Date;
  expiresAt: Date;
  
  // Sharing settings
  sharedWith: SharedRouteRecipient[];
  isPublic: boolean; // Anyone in tenant can access
  shareCode?: string; // 6-digit code for easy sharing
  
  // Status
  status: 'active' | 'expired' | 'completed';
  viewCount: number;
  lastViewedAt?: Date;
  
  // Tenant isolation
  companyId: string;
}

export interface SharedRouteWell {
  wellId: string;
  wellName: string;
  order: number;
  coordinates: { lat: number; lng: number };
  estimatedArrival?: string; // ISO timestamp
  estimatedTime?: number; // hours from previous stop
  distance?: number; // miles from previous stop
  notes?: string;
  tasks?: string[]; // Task IDs to complete at this well
}

export interface SharedRouteRecipient {
  uid: string;
  name: string;
  email?: string;
  phone?: string;
  sentAt: Date;
  viewedAt?: Date;
  accepted?: boolean;
  completedAt?: Date;
}

export interface CreateSharedRouteInput {
  name: string;
  description?: string;
  wells: SharedRouteWell[];
  totalDistance: number;
  estimatedTime: number;
  optimizationMetrics?: SharedRoute['optimizationMetrics'];
  sharedWith?: { uid: string; name: string }[];
  isPublic?: boolean;
  expiresInHours?: number;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Generate a 6-digit share code
 */
const generateShareCode = (): string => {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
};

/**
 * Get collection reference for shared routes
 */
const getSharedRoutesRef = () => {
  const companyId = getCompanyId();
  return collection(db, `companies/${companyId}/shared_routes`);
};

// ============================================================================
// CREATE & SHARE ROUTES
// ============================================================================

/**
 * Create a shareable route
 */
export const createSharedRoute = async (
  input: CreateSharedRouteInput,
  createdBy: { uid: string; name: string; role?: string }
): Promise<SharedRoute> => {
  const companyId = getCompanyId();
  const routesRef = getSharedRoutesRef();
  const routeDoc = doc(routesRef);
  
  const expiresInHours = input.expiresInHours || 24;
  const now = new Date();
  const expiresAt = new Date(now.getTime() + expiresInHours * 60 * 60 * 1000);
  
  const sharedRoute: SharedRoute = {
    id: routeDoc.id,
    name: input.name,
    description: input.description,
    wells: input.wells,
    totalDistance: input.totalDistance,
    estimatedTime: input.estimatedTime,
    optimizationMetrics: input.optimizationMetrics,
    createdBy,
    createdAt: now,
    expiresAt,
    sharedWith: (input.sharedWith || []).map(u => ({
      uid: u.uid,
      name: u.name,
      sentAt: now,
    })),
    isPublic: input.isPublic ?? true,
    shareCode: generateShareCode(),
    status: 'active',
    viewCount: 0,
    companyId,
  };
  
  await setDoc(routeDoc, {
    ...sharedRoute,
    createdAt: Timestamp.fromDate(sharedRoute.createdAt),
    expiresAt: Timestamp.fromDate(sharedRoute.expiresAt),
    sharedWith: sharedRoute.sharedWith.map(r => ({
      ...r,
      sentAt: Timestamp.fromDate(r.sentAt),
    })),
  });
  
  return sharedRoute;
};

/**
 * Share a route with specific users
 */
export const shareRouteWithUsers = async (
  routeId: string,
  users: { uid: string; name: string }[]
): Promise<void> => {
  const routeRef = doc(getSharedRoutesRef(), routeId);
  const routeSnap = await getDoc(routeRef);
  
  if (!routeSnap.exists()) {
    throw new Error('Route not found');
  }
  
  const route = routeSnap.data() as SharedRoute;
  const now = new Date();
  
  const newRecipients: SharedRouteRecipient[] = users
    .filter(u => !route.sharedWith.some(r => r.uid === u.uid))
    .map(u => ({
      uid: u.uid,
      name: u.name,
      sentAt: now,
    }));
  
  if (newRecipients.length === 0) {
    toast.info('All selected users already have access');
    return;
  }
  
  await updateDoc(routeRef, {
    sharedWith: [
      ...route.sharedWith,
      ...newRecipients.map(r => ({
        ...r,
        sentAt: Timestamp.fromDate(r.sentAt),
      })),
    ],
  });
  
  toast.success(`Route shared with ${newRecipients.length} user(s)`);
  
  // TODO: Send push notifications to recipients
};

// ============================================================================
// RETRIEVE ROUTES
// ============================================================================

/**
 * Get a shared route by ID
 */
export const getSharedRoute = async (routeId: string): Promise<SharedRoute | null> => {
  try {
    const routeRef = doc(getSharedRoutesRef(), routeId);
    const routeSnap = await getDoc(routeRef);
    
    if (!routeSnap.exists()) return null;
    
    const data = routeSnap.data();
    
    // Check expiration
    const expiresAt = data.expiresAt.toDate();
    if (expiresAt < new Date() && data.status === 'active') {
      await updateDoc(routeRef, { status: 'expired' });
      data.status = 'expired';
    }
    
    return {
      ...data,
      createdAt: data.createdAt.toDate(),
      expiresAt: data.expiresAt.toDate(),
      sharedWith: data.sharedWith.map((r: any) => ({
        ...r,
        sentAt: r.sentAt.toDate(),
        viewedAt: r.viewedAt?.toDate(),
        completedAt: r.completedAt?.toDate(),
      })),
      lastViewedAt: data.lastViewedAt?.toDate(),
    } as SharedRoute;
  } catch (error) {
    console.error('Failed to get shared route:', error);
    return null;
  }
};

/**
 * Get a shared route by share code
 */
export const getRouteByShareCode = async (shareCode: string): Promise<SharedRoute | null> => {
  try {
    const routesRef = getSharedRoutesRef();
    const q = query(routesRef, where('shareCode', '==', shareCode.toUpperCase()));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const routeDoc = snapshot.docs[0];
    return getSharedRoute(routeDoc.id);
  } catch (error) {
    console.error('Failed to get route by share code:', error);
    return null;
  }
};

/**
 * Get routes shared with a specific user
 */
export const getRoutesSharedWithUser = async (userId: string): Promise<SharedRoute[]> => {
  try {
    const routesRef = getSharedRoutesRef();
    const q = query(
      routesRef,
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const routes: SharedRoute[] = [];
    
    for (const doc of snapshot.docs) {
      const route = await getSharedRoute(doc.id);
      if (route && (route.isPublic || route.sharedWith.some(r => r.uid === userId))) {
        routes.push(route);
      }
    }
    
    return routes;
  } catch (error) {
    console.error('Failed to get shared routes:', error);
    return [];
  }
};

/**
 * Get routes created by a user
 */
export const getRoutesCreatedByUser = async (userId: string): Promise<SharedRoute[]> => {
  try {
    const routesRef = getSharedRoutesRef();
    const q = query(
      routesRef,
      where('createdBy.uid', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const routes: SharedRoute[] = [];
    
    for (const doc of snapshot.docs) {
      const route = await getSharedRoute(doc.id);
      if (route) routes.push(route);
    }
    
    return routes;
  } catch (error) {
    console.error('Failed to get created routes:', error);
    return [];
  }
};

// ============================================================================
// UPDATE & TRACK ROUTES
// ============================================================================

/**
 * Record that a user viewed a shared route
 */
export const recordRouteView = async (routeId: string, userId: string): Promise<void> => {
  try {
    const routeRef = doc(getSharedRoutesRef(), routeId);
    const routeSnap = await getDoc(routeRef);
    
    if (!routeSnap.exists()) return;
    
    const data = routeSnap.data();
    const now = new Date();
    
    // Update view count and last viewed
    const updates: any = {
      viewCount: (data.viewCount || 0) + 1,
      lastViewedAt: Timestamp.fromDate(now),
    };
    
    // Mark recipient as viewed
    const recipientIndex = data.sharedWith.findIndex((r: any) => r.uid === userId);
    if (recipientIndex >= 0 && !data.sharedWith[recipientIndex].viewedAt) {
      data.sharedWith[recipientIndex].viewedAt = Timestamp.fromDate(now);
      updates.sharedWith = data.sharedWith;
    }
    
    await updateDoc(routeRef, updates);
  } catch (error) {
    console.error('Failed to record route view:', error);
  }
};

/**
 * Mark a recipient as having accepted/started the route
 */
export const acceptSharedRoute = async (routeId: string, userId: string): Promise<void> => {
  try {
    const routeRef = doc(getSharedRoutesRef(), routeId);
    const routeSnap = await getDoc(routeRef);
    
    if (!routeSnap.exists()) return;
    
    const data = routeSnap.data();
    const recipientIndex = data.sharedWith.findIndex((r: any) => r.uid === userId);
    
    if (recipientIndex >= 0) {
      data.sharedWith[recipientIndex].accepted = true;
      await updateDoc(routeRef, { sharedWith: data.sharedWith });
    }
  } catch (error) {
    console.error('Failed to accept route:', error);
  }
};

/**
 * Mark a route as completed by a recipient
 */
export const completeSharedRoute = async (routeId: string, userId: string): Promise<void> => {
  try {
    const routeRef = doc(getSharedRoutesRef(), routeId);
    const routeSnap = await getDoc(routeRef);
    
    if (!routeSnap.exists()) return;
    
    const data = routeSnap.data();
    const recipientIndex = data.sharedWith.findIndex((r: any) => r.uid === userId);
    
    if (recipientIndex >= 0) {
      data.sharedWith[recipientIndex].completedAt = Timestamp.fromDate(new Date());
      await updateDoc(routeRef, { sharedWith: data.sharedWith });
      
      // Check if all recipients completed
      const allCompleted = data.sharedWith.every((r: any) => r.completedAt);
      if (allCompleted) {
        await updateDoc(routeRef, { status: 'completed' });
      }
    }
  } catch (error) {
    console.error('Failed to complete route:', error);
  }
};

// ============================================================================
// DELETE ROUTES
// ============================================================================

/**
 * Delete a shared route
 */
export const deleteSharedRoute = async (routeId: string): Promise<void> => {
  try {
    const routeRef = doc(getSharedRoutesRef(), routeId);
    await deleteDoc(routeRef);
  } catch (error) {
    console.error('Failed to delete route:', error);
    throw error;
  }
};

// ============================================================================
// SHARE UTILITIES
// ============================================================================

/**
 * Generate a shareable URL for a route
 */
export const generateShareUrl = (routeId: string): string => {
  // Use the current origin or fallback
  const origin = typeof window !== 'undefined' 
    ? window.location.origin 
    : 'https://oilwellx.web.app';
  return `${origin}/route/${routeId}`;
};

/**
 * Generate a shareable text message
 */
export const generateShareText = (route: SharedRoute): string => {
  const url = generateShareUrl(route.id);
  return `🚛 Route Assignment: ${route.name}\n\n` +
    `📍 ${route.wells.length} stops\n` +
    `📏 ${route.totalDistance.toFixed(1)} miles\n` +
    `⏱️ Est. ${route.estimatedTime.toFixed(1)} hours\n\n` +
    `Code: ${route.shareCode}\n` +
    `Link: ${url}`;
};

/**
 * Copy share text to clipboard
 */
export const copyShareToClipboard = async (route: SharedRoute): Promise<void> => {
  const text = generateShareText(route);
  await navigator.clipboard.writeText(text);
  toast.success('Route link copied to clipboard');
};

/**
 * Share route via native share API (mobile)
 */
export const nativeShare = async (route: SharedRoute): Promise<boolean> => {
  if (!navigator.share) return false;
  
  try {
    await navigator.share({
      title: `Route: ${route.name}`,
      text: generateShareText(route),
      url: generateShareUrl(route.id),
    });
    return true;
  } catch (error) {
    // User cancelled or share failed
    return false;
  }
};

// ============================================================================
// EXPORT SERVICE
// ============================================================================

export const routeSharingService = {
  createSharedRoute,
  shareRouteWithUsers,
  getSharedRoute,
  getRouteByShareCode,
  getRoutesSharedWithUser,
  getRoutesCreatedByUser,
  recordRouteView,
  acceptSharedRoute,
  completeSharedRoute,
  deleteSharedRoute,
  generateShareUrl,
  generateShareText,
  copyShareToClipboard,
  nativeShare,
};

export default routeSharingService;
